import Head from "next/head";
import React from "react";
import {buildImageSrc} from "./buildImageSrc";

export interface HreflangAlternate {
  hreflang: string;
  href: string;
}

interface SeoMetaProps {
  title?: string;
  subtitle?: string;
  description?: string;
  keywords?: string[];
  image?: string;
  imageWidth?: number;
  imageHeight?: number;
  url?: string;
  urlCanonical?: string;
  type?: string;
  twitterCard?: string;
  /**
   * `<link rel="alternate" hreflang="..." href="...">` para site com o mesmo
   * conteúdo em vários idiomas. A lib só renderiza o que recebe: resolver quais
   * idiomas existem e montar a URL absoluta é do consumidor.
   */
  hreflangAlternates?: HreflangAlternate[];
  /** Separador entre `title` e `subtitle` no `<title>`. Default `' - '`. */
  titleSeparator?: string;
  /**
   * Nome legível do site, emitido como `og:site_name` — o rótulo de origem do
   * card social (Facebook, LinkedIn, Slack, WhatsApp). Sem ele cada plataforma
   * cai num fallback próprio, normalmente o domínio cru. Sem default.
   *
   * Não é derivado de `subtitle` de propósito: `subtitle` é o sufixo do
   * `<title>` e pode carregar mais que a marca (`"Central de Ajuda | Marca"`).
   */
  siteName?: string;
  /** `@handle` da conta do site no X/Twitter, emitido como `twitter:site`. Sem default. */
  twitterSite?: string;
}

/**
 * Junta `title` e `subtitle` sem nunca deixar separador solto.
 *
 * Até a 2.0.0 isto era `${titleParse} - ${subtitle}`, que produzia
 * `<title>Criar pasta - </title>` sempre que o consumidor não passava
 * `subtitle` — o default `''` entrava e o traço ficava lá.
 */
export function composeTitle(
  title?: string,
  subtitle?: string,
  separator: string = ' - '
): string {
  return [title, subtitle].filter(Boolean).join(separator);
}

const SeoMeta: React.FC<SeoMetaProps> = ({
  title,
  subtitle = '',
  description,
  keywords,
  image,
  imageWidth = 1200,
  imageHeight = 630,
  url,
  urlCanonical,
  type,
  twitterCard = 'summary',
  hreflangAlternates,
  titleSeparator = ' - ',
  siteName,
  twitterSite,
}) => {
  // `title` vai inteiro para o `<title>`, sem truncar.
  //
  // Até a 2.0.0 havia um `substring(0, 55) + '...'` acima de 60 caracteres. Era
  // opinativo (60 medido como? em qual fonte?) e rígido (um número só, sem saber
  // se o destino é a aba do navegador, o resultado de busca ou um card social —
  // cada um com limite próprio). Pior: o corte acontecia ANTES de concatenar o
  // `subtitle`, então o `<title>` final passava de 90 caracteres mesmo com o
  // título já mutilado — reticências no meio de uma palavra seguidas de mais
  // texto. Quem corta por comprimento (o Google, por pixel) corta do fim para
  // trás, onde está a marca, não o título específico da página. Deixar o corte
  // com quem exibe é estritamente melhor.
  //
  // `descriptionParse` continua: não concatena nada depois, e o limite de 160
  // para descrição é convenção estável entre buscadores.
  const descriptionParse = description && description.length > 160 ? `${description.substring(0, 160)}...` : description;
  const keywordsString = Array.isArray(keywords) ? keywords.join(', ') : '';

  const fullTitle = composeTitle(title, subtitle, titleSeparator);

  // Só no servidor: em build time isto aparece uma vez por página no log; no
  // browser seria ruído a cada render/hidratação.
  if (typeof window === 'undefined' && title && !subtitle) {
    console.warn(
      `[wp-nextjs-tools] SeoMeta: "${title}" recebeu \`title\` sem \`subtitle\` — ` +
      'o <title> sai só com o título da página, sem o sufixo do site. ' +
      'Passe `subtitle` (ex.: "Central de Ajuda | Minha Marca") ou ignore se for intencional.'
    );
  }

  const facebookAppId = process.env.NEXT_PUBLIC_FACEBOOK_APP_ID;

  return (
    <>
      <Head>
        {/* Título e Meta Tags básicas */}
        <title>{fullTitle}</title>
        {descriptionParse && <meta name="description" content={descriptionParse} />}
        {keywords && <meta name="keywords" content={keywordsString} />}

        {/* Open Graph Meta Tags */}
        {title && <meta property="og:title" content={title} />}
        {descriptionParse && <meta property="og:description" content={descriptionParse} />}
        {image && <meta property="og:image" content={buildImageSrc(image)} />}
        {image && <meta property="og:image:width" content={String(imageWidth)} />}
        {image && <meta property="og:image:height" content={String(imageHeight)} />}
        {url && <meta property="og:url" content={`${buildImageSrc(url)}/`} />}
        <meta property="og:type" content={type || 'website'} />
        {siteName && <meta property="og:site_name" content={siteName} />}
        {facebookAppId && <meta property="fb:app_id" content={facebookAppId} />}

        {/* Twitter Card Meta Tags */}
        <meta name="twitter:card" content={twitterCard} />
        {twitterSite && <meta name="twitter:site" content={twitterSite} />}
        {title && <meta name="twitter:title" content={title} />}
        {descriptionParse && <meta name="twitter:description" content={descriptionParse} />}
        {image && <meta name="twitter:image" content={buildImageSrc(image)} />}
        {url && <meta name="twitter:url" content={buildImageSrc(url)} />}

        {/* Canonical URL — a condição testa a mesma prop que o href usa.
            Até a 2.0.0 a condição era `url` e o href `urlCanonical`: quem passava
            só `urlCanonical` (wp-blog, wp-cases) não recebia canonical nenhum, e
            quem passava só `url` recebia um href vazio. */}
        {urlCanonical && <link rel="canonical" href={buildImageSrc(urlCanonical)} />}

        {/* Alternativas por idioma */}
        {hreflangAlternates?.map(({ hreflang, href }) => (
          // `hrefLang` (camelCase) é o nome que o React espera. Ele emite o
          // atributo com esse mesmo case (`hrefLang="en"` no HTML, verificado no
          // react-dom 18.3.1 e 19.1.0) — o que é válido, porque nome de atributo
          // HTML é case-insensitive e o parser lê como `hreflang`. Escrever
          // `hreflang` minúsculo no JSX renderiza igual, mas faz o React logar
          // "Invalid DOM property" a cada render.
          <link rel="alternate" hrefLang={hreflang} href={href} key={hreflang} />
        ))}
      </Head>
    </>
  );
};

export default SeoMeta;
